/**
 * WagePro-branded ESS invite-accept screen — the emailed set-password LINK
 * channel (WagePro signup epic).
 *
 * Lands here from the link `payroll.createEssInvite` mints
 * (`${WAGEPRO_APP_BASE_URL}/invite/accept?token=...&entity_id=...`). On mount,
 * looks up the invite via `essInviteSignup.lookup` (no auth required) to show
 * "Join {company_name}" with the invited email locked, then hands over to the
 * forced set-password step (`invite_set_password_form.tsx`), shared with the
 * one-time-code channel (`invite_code_screen.tsx`):
 *
 *   - **exists** → password-only field ("sign in and accept")
 *   - **new**    → choose a password + signup fields ("create account and accept")
 *
 * Submitting calls `essInviteSignup.acceptWithCredentials`, then reboots the
 * app from the new session (cookie on web, persisted bearer in the native
 * shell). It deliberately does NOT run the "Sign in with Ledger" PKCE handoff:
 * `GET /oauth/authorize` lands on Ledger's consent screen, whose organisation
 * picker lists firms the caller is a MEMBER of — an ESS employee is a member
 * of none, so that handoff can never complete for them. `resolveAuthBoot`
 * (`lib/auth/auth_context.tsx`) resolves the whole session directly instead.
 *
 * `apps/tax-web`'s `/ess/accept` screen (AAL2-session-only, via
 * `personalPayroll.acceptEssInvite`) is left unchanged — old links minted
 * before the domain fix still work there, and die within the invite expiry.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from '@mukil/i18n';
import { GlassCard } from '@jumpstone/ui-react';
import { getPublicAccountTrpcClient } from '../../lib/auth/public_account_trpc.js';
import {
  classifyAuthError,
  type AuthErrorDisplay,
} from '../../lib/auth/auth_error_display.js';
import { parseInviteAcceptParams } from '../../lib/auth/invite_accept_logic.js';
import { AuthFormError } from './auth_form_error.js';
import { InviteSetPasswordForm } from './invite_set_password_form.js';

interface LookupResult {
  invited_email: string;
  company_name: string;
  account_exists: boolean;
  already_expired: boolean;
}

type LookupState =
  | { kind: 'loading' }
  | { kind: 'invalid_link' }
  | { kind: 'error'; error: AuthErrorDisplay }
  | { kind: 'ready'; result: LookupResult };

export function InviteAcceptScreen({
  onAccepted,
}: {
  /** Called after the accept succeeds, right before the app reboots from the new session. */
  onAccepted?: () => void;
}): React.JSX.Element {
  const { t } = useTranslation('wp');
  const params = parseInviteAcceptParams(window.location.search);
  const [lookup, setLookup] = useState<LookupState>({ kind: 'loading' });

  useEffect(() => {
    if (!params) {
      setLookup({ kind: 'invalid_link' });
      return;
    }
    // Narrow into plain locals — TS does not carry the `!params` narrowing
    // above into a nested function declaration that closes over `params`.
    const { entity_id, token } = params;
    let cancelled = false;
    async function runLookup(): Promise<void> {
      try {
        const client = getPublicAccountTrpcClient();
        const result = await client.essInviteSignup.lookup.query({
          entity_id,
          token,
        });
        if (!cancelled) setLookup({ kind: 'ready', result });
      } catch (err) {
        if (!cancelled) {
          setLookup({ kind: 'error', error: classifyAuthError(err) });
        }
      }
    }
    void runLookup();
    return () => {
      cancelled = true;
    };
    // params is derived once from window.location.search at mount time —
    // re-running this effect on every render would refetch on unrelated
    // state changes. `react-hooks/exhaustive-deps` is off repo-wide
    // (packages/config-eslint/react.js), so no disable directive is needed.
  }, []);

  return (
    <div className="min-h-dvh flex items-center justify-center p-6">
      <GlassCard
        variant="strong"
        className="rounded-2xl p-8 max-w-[480px] w-full"
      >
        {lookup.kind === 'loading' && (
          <p className="text-sm text-[color:var(--ink-muted)]">
            {t('invite_accept.loading')}
          </p>
        )}

        {lookup.kind === 'invalid_link' && (
          <p role="alert" className="text-sm text-[var(--color-danger)]">
            {t('invite_accept.invalid_link')}
          </p>
        )}

        {lookup.kind === 'error' && <AuthFormError error={lookup.error} />}

        {lookup.kind === 'ready' && lookup.result.already_expired && (
          <p role="alert" className="text-sm text-[var(--color-danger)]">
            {t('invite_accept.expired')}
          </p>
        )}

        {lookup.kind === 'ready' &&
          !lookup.result.already_expired &&
          params && (
            <InviteSetPasswordForm
              credential={{
                kind: 'link',
                entity_id: params.entity_id,
                token: params.token,
              }}
              invitedEmail={lookup.result.invited_email}
              companyName={lookup.result.company_name}
              accountExists={lookup.result.account_exists}
              onAccepted={onAccepted}
            />
          )}
      </GlassCard>
    </div>
  );
}
